import { ChartDataPoint } from '../services/PDFExportService.backend';

// Chart export utilities for PDF integration
export interface ChartExportOptions {
  width?: number;
  height?: number;
  backgroundColor?: string;
  textColor?: string;
  gridColor?: string;
  lineColor?: string;
  pointColor?: string;
}

export interface ChartImageResult {
  imageData: string; // Base64 image data
  width: number;
  height: number;
  mimeType: string;
}

// Export chart as image for PDF integration
export const exportChartAsImage = async (
  chartData: ChartDataPoint[],
  chartType: 'line' | 'bar' | 'pie' = 'line',
  options: ChartExportOptions = {}
): Promise<ChartImageResult> => {
  const {
    width = 800,
    height = 400,
    backgroundColor = '#ffffff',
    textColor = '#374151',
    gridColor = '#e5e7eb',
    lineColor = '#2563eb',
    pointColor = '#10b981'
  } = options;

  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Unable to create canvas context');
  }

  // Set background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);

  // Draw chart based on type
  switch (chartType) {
    case 'line':
      return drawLineChart(ctx, canvas, chartData, {
        width, height, textColor, gridColor, lineColor, pointColor
      });
    case 'bar':
      return drawBarChart(ctx, canvas, chartData, {
        width, height, textColor, gridColor, lineColor
      });
    case 'pie':
      return drawPieChart(ctx, canvas, chartData, {
        width, height, textColor, gridColor
      });
    default:
      throw new Error(`Unsupported chart type: ${chartType}`);
  }
};

// Draw line chart
function drawLineChart(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  data: ChartDataPoint[],
  options: Required<ChartExportOptions>
): ChartImageResult {
  const { width, height, textColor, gridColor, lineColor, pointColor } = options;

  // Calculate margins
  const margin = 40;
  const chartWidth = width - margin * 2;
  const chartHeight = height - margin * 2;

  // Draw axes
  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 1;
  
  // X and Y axes
  ctx.beginPath();
  ctx.moveTo(margin, margin);
  ctx.lineTo(margin, height - margin);
  ctx.lineTo(width - margin, height - margin);
  ctx.stroke();

  // Draw grid lines
  ctx.strokeStyle = gridColor;
  ctx.setLineDash([2, 2]);
  
  // Horizontal grid lines
  for (let i = 0; i <= 5; i++) {
    const y = margin + (chartHeight * i) / 5;
    ctx.beginPath();
    ctx.moveTo(margin, y);
    ctx.lineTo(width - margin, y);
    ctx.stroke();
  }
  
  // Vertical grid lines
  for (let i = 0; i <= data.length - 1; i++) {
    const x = margin + (chartWidth * i) / (data.length - 1);
    ctx.beginPath();
    ctx.moveTo(x, margin);
    ctx.lineTo(x, height - margin);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // Calculate data ranges
  const revenueValues = data.map(d => d.revenue);
  const maxRevenue = Math.max(...revenueValues);
  const minRevenue = Math.min(...revenueValues);
  const revenueRange = maxRevenue - minRevenue || 1;

  // Draw line
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 2;
  ctx.beginPath();

  data.forEach((point, index) => {
    const x = margin + (chartWidth * index) / (data.length - 1);
    const y = height - margin - ((point.revenue - minRevenue) / revenueRange) * chartHeight;

    if (index === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  });
  ctx.stroke();

  // Draw points
  ctx.fillStyle = pointColor;
  data.forEach((point, index) => {
    const x = margin + (chartWidth * index) / (data.length - 1);
    const y = height - margin - ((point.revenue - minRevenue) / revenueRange) * chartHeight;

    ctx.beginPath();
    ctx.arc(x, y, 3, 0, 2 * Math.PI);
    ctx.fill();
  });

  // Add labels
  ctx.fillStyle = textColor;
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';

  // X-axis labels
  data.forEach((point, index) => {
    const x = margin + (chartWidth * index) / (data.length - 1);
    ctx.fillText(point.time, x, height - margin + 20);
  });

  // Y-axis labels
  for (let i = 0; i <= 5; i++) {
    const value = minRevenue + (revenueRange * i) / 5;
    const y = height - margin - (chartHeight * i) / 5;
    ctx.textAlign = 'right';
    ctx.fillText(`GH₵${Math.round(value).toLocaleString()}`, margin - 10, y + 5);
  }

  // Add title
  ctx.fillStyle = textColor;
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Revenue Trend', width / 2, margin / 2);

  return {
    imageData: canvas.toDataURL('image/png'),
    width,
    height,
    mimeType: 'image/png'
  };
}

// Draw bar chart
function drawBarChart(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  data: ChartDataPoint[],
  options: Required<ChartExportOptions>
): ChartImageResult {
  const { width, height, textColor, gridColor, lineColor } = options;

  const margin = 40;
  const chartWidth = width - margin * 2;
  const chartHeight = height - margin * 2;

  // Draw axes
  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(margin, margin);
  ctx.lineTo(margin, height - margin);
  ctx.lineTo(width - margin, height - margin);
  ctx.stroke();

  // Draw grid lines
  ctx.strokeStyle = gridColor;
  ctx.setLineDash([2, 2]);
  
  for (let i = 0; i <= 5; i++) {
    const y = margin + (chartHeight * i) / 5;
    ctx.beginPath();
    ctx.moveTo(margin, y);
    ctx.lineTo(width - margin, y);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // Calculate data ranges
  const memberValues = data.map(d => d.members);
  const maxMembers = Math.max(...memberValues);
  const minMembers = Math.min(...memberValues);
  const memberRange = maxMembers - minMembers || 1;

  // Draw bars
  const barWidth = chartWidth / data.length * 0.6;
  const barSpacing = chartWidth / data.length;

  data.forEach((point, index) => {
    const x = margin + index * barSpacing + barSpacing * 0.2;
    const barHeight = (point.members / maxMembers) * chartHeight;
    const y = height - margin - barHeight;

    // Draw bar
    ctx.fillStyle = lineColor;
    ctx.fillRect(x, y, barWidth, barHeight);

    // Add value label
    ctx.fillStyle = textColor;
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(point.members.toString(), x + barWidth / 2, y - 5);
  });

  // Add labels
  ctx.fillStyle = textColor;
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';

  data.forEach((point, index) => {
    const x = margin + index * barSpacing + barSpacing / 2;
    ctx.fillText(point.time, x, height - margin + 20);
  });

  // Y-axis labels
  for (let i = 0; i <= 5; i++) {
    const value = minMembers + (memberRange * i) / 5;
    const y = height - margin - (chartHeight * i) / 5;
    ctx.textAlign = 'right';
    ctx.fillText(Math.round(value).toString(), margin - 10, y + 5);
  }

  // Add title
  ctx.fillStyle = textColor;
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Member Count Trend', width / 2, margin / 2);

  return {
    imageData: canvas.toDataURL('image/png'),
    width,
    height,
    mimeType: 'image/png'
  };
}

// Draw pie chart
function drawPieChart(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  data: ChartDataPoint[],
  options: Required<ChartExportOptions>
): ChartImageResult {
  const { width, height, textColor, gridColor } = options;

  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 3;

  // Calculate totals
  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0);
  
  if (totalRevenue === 0) {
    // Draw empty pie chart
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 2;
    ctx.stroke();
    
    ctx.fillStyle = textColor;
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('No Data Available', centerX, centerY);
    
    return {
      imageData: canvas.toDataURL('image/png'),
      width,
      height,
      mimeType: 'image/png'
    };
  }

  // Draw pie chart segments
  let startAngle = 0;
  const colors = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  data.forEach((point, index) => {
    const percentage = point.revenue / totalRevenue;
    const endAngle = startAngle + (percentage * 2 * Math.PI);

    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, endAngle);
    ctx.closePath();

    ctx.fillStyle = colors[index % colors.length];
    ctx.fill();

    // Add labels
    const labelAngle = (startAngle + endAngle) / 2;
    const labelRadius = radius * 0.7;
    const labelX = centerX + Math.cos(labelAngle) * labelRadius;
    const labelY = centerY + Math.sin(labelAngle) * labelRadius;

    ctx.fillStyle = textColor;
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(
      `${point.time}: ${Math.round(percentage * 100)}%`, 
      labelX, 
      labelY
    );

    startAngle = endAngle;
  });

  // Add title
  ctx.fillStyle = textColor;
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Revenue Distribution', centerX, centerY - radius - 20);

  return {
    imageData: canvas.toDataURL('image/png'),
    width,
    height,
    mimeType: 'image/png'
  };
}

// Generate chart PDF with title
export const generateChartPDF = async (
  chartData: ChartDataPoint[],
  chartType: 'line' | 'bar' | 'pie',
  title: string,
  options: ChartExportOptions = {}
): Promise<Blob> => {
  try {
    const jsPDF = (await import('jspdf')).default;
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Export chart as image
    const chartResult = await exportChartAsImage(chartData, chartType, {
      width: 800,
      height: 400,
      ...options
    });

    // Add title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(37, 99, 235);
    doc.text(title, pageWidth / 2, 20, { align: 'center' });

    // Add subtitle
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(31, 41, 55);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 30, { align: 'center' });

    // Add chart image
    const imgWidth = 200;
    const imgHeight = (chartResult.height * imgWidth) / chartResult.width;
    const imgX = (pageWidth - imgWidth) / 2;
    const imgY = 40;

    doc.addImage(chartResult.imageData, 'PNG', imgX, imgY, imgWidth, imgHeight);

    // Add data table
    const tableY = imgY + imgHeight + 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(37, 99, 235);
    doc.text('Data Summary', 20, tableY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(31, 41, 55);

    let currentY = tableY + 10;
    chartData.forEach((point, index) => {
      if (currentY > pageHeight - 20) {
        doc.addPage();
        currentY = 20;
      }

      const rowData = [
        point.time,
        `GH₵${point.revenue.toLocaleString()}`,
        point.members.toString(),
        point.activities.toString()
      ].join(' | ');

      doc.text(rowData, 20, currentY);
      currentY += 8;
    });

    // Add footer
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(156, 163, 175);
    doc.text('Generated by Ghana Town Membership System', 20, pageHeight - 10);

    return new Blob([doc.output('blob')], { type: 'application/pdf' });
  } catch (error) {
    console.error('Chart PDF generation error:', error);
    throw new Error('Failed to generate chart PDF. Please try again.');
  }
};

// Export chart data summary
export const getChartSummary = (chartData: ChartDataPoint[]): {
  totalRevenue: number;
  totalMembers: number;
  totalActivities: number;
  averageRevenue: number;
  maxRevenue: number;
  minRevenue: number;
} => {
  const totalRevenue = chartData.reduce((sum, d) => sum + d.revenue, 0);
  const totalMembers = chartData.reduce((sum, d) => sum + d.members, 0);
  const totalActivities = chartData.reduce((sum, d) => sum + d.activities, 0);
  const averageRevenue = totalRevenue / chartData.length;
  const maxRevenue = Math.max(...chartData.map(d => d.revenue));
  const minRevenue = Math.min(...chartData.map(d => d.revenue));

  return {
    totalRevenue,
    totalMembers,
    totalActivities,
    averageRevenue,
    maxRevenue,
    minRevenue
  };
};